import React, { useEffect, useState, useRef } from 'react';
import {
  GoogleMap,
  useLoadScript,
  DirectionsRenderer,
  Marker,
  Polyline
} from '@react-google-maps/api';
import api from '../../api/api';

const libraries = ['places'];

const containerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = {
  lat: 30.7046,
  lng: 76.7179
};

const EmployeeRouteMap = () => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyB1G6mcpzOU-3I0pT6VRM7l4itDeZdxJcc',
    libraries
  });

  const [employeeRoutes, setEmployeeRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // default today
  const mapRef = useRef(null);

  useEffect(() => {
    if (!isLoaded) return;

    const fetchData = async () => {
      try {
        const res = await api.get('/live-location/user');
        const data = res.data?.data || [];

        // ✅ Filter only selected date data
        const filteredData = data.filter(loc => {
          const locDate = new Date(loc.created_at).toISOString().split('T')[0];
          return locDate === selectedDate;
        });

        const grouped = {};

        // Group by employee_name
        filteredData.forEach(loc => {
          const key = loc.employee_name;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
            name: loc.employee_name,
            time: loc.created_at,
            phone: loc.phone
          });
        });

        setEmployeeRoutes([]); // clear previous routes before setting new

        Object.entries(grouped).forEach(([name, employeeLocations]) => {
          if (employeeLocations.length >= 2) {
            const sorted = employeeLocations.sort(
              (a, b) => new Date(a.time) - new Date(b.time)
            );
            const origin = sorted[0];
            const destination = sorted[sorted.length - 1];

            if (
              origin.lat === destination.lat &&
              origin.lng === destination.lng
            ) {
              setEmployeeRoutes(prev => [
                ...prev,
                {
                  name,
                  origin,
                  destination,
                  directions: null,
                  path: sorted,
                  phone: sorted[0].phone
                }
              ]);
              return;
            }

            const directionsService = new window.google.maps.DirectionsService();
            directionsService.route(
              {
                origin,
                destination,
                travelMode: window.google.maps.TravelMode.DRIVING
              },
              (result, status) => {
                if (status === 'OK') {
                  setEmployeeRoutes(prev => [
                    ...prev,
                    {
                      name,
                      origin,
                      destination,
                      directions: result,
                      path: sorted,
                      phone: sorted[0].phone
                    }
                  ]);
                } else {
                  console.error('Route error for', name, ':', status);
                  setEmployeeRoutes(prev => [
                    ...prev,
                    {
                      name,
                      origin,
                      destination,
                      directions: null,
                      path: sorted,
                      phone: sorted[0].phone
                    }
                  ]);
                }
              }
            );
          }
        });
      } catch (error) {
        console.error('API error:', error);
      }
    };

    fetchData();
  }, [isLoaded, selectedDate]);

  // Filter routes by search term on employee_name or phone
  const filteredRoutes = employeeRoutes.filter(route => {
    const term = searchTerm.toLowerCase();
    return (
      route.name.toLowerCase().includes(term) ||
      (route.phone && route.phone.toLowerCase().includes(term))
    );
  });

  return isLoaded ? (
    <>
      {/* Filters UI */}
      <div style={{ position: 'absolute', top: 70, right: 15, zIndex: 999, backgroundColor: 'white', padding: '12px', borderRadius: '4px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          style={{ padding: '6px' }}
          max={new Date().toISOString().split('T')[0]} // no future dates
        />

        <input
          type="text"
          placeholder="Search by employee name or phone"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ width: '250px', padding: '6px' }}
        />
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={14}
        onLoad={map => (mapRef.current = map)}
      >
        {filteredRoutes.map((route, index) => (
          <React.Fragment key={index}>
            {route.directions && (
              <DirectionsRenderer
                directions={route.directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#0b1edf',
                    strokeOpacity: 0.9,
                    strokeWeight: 10
                  }
                }}
              />
            )}

            {!route.directions && route.path && (
              <Polyline
                path={route.path}
                options={{
                  strokeColor: '#ff0000',
                  strokeOpacity: 0.8,
                  strokeWeight: 4
                }}
              />
            )}

            {route.path &&
              route.path.map((point, i) => (
                <Marker
                  key={i}
                  position={{ lat: point.lat, lng: point.lng }}
                  label={(i + 1).toString()}
                  title={`Time: ${point.time}`}
                />
              ))}

            <Marker
              position={route.origin}
              label="S"
              title={`Start: ${route.name}`}
            />

            {route.directions || route.origin !== route.destination ? (
              <Marker
                position={route.destination}
                label="E"
                title={`End: ${route.name}`}
              />
            ) : null}
          </React.Fragment>
        ))}
      </GoogleMap>
    </>
  ) : (
    <div>Loading map...</div>
  );
};

export default EmployeeRouteMap;
